import { useQueryClient } from '@tanstack/react-query'
import {
  providersUserPeople,
  ProvidersUserPeoplePartialUpdateBodyParams,
  ProvidersUserPeoplePartialUpdateResponse,
} from '@fireflyhealth/api'

import { useMutation, useQuery } from '~/shared/hooks/query'

// Callers pass snake_case keys validated against the generated body schema.
// `id` is split out of the body and supplied as a URL path param by
// `updatePerson` below.
type UpdatePersonPayload = {
  id: number
} & Partial<ProvidersUserPeoplePartialUpdateBodyParams>

async function updatePerson(
  payload: UpdatePersonPayload,
): Promise<ProvidersUserPeoplePartialUpdateResponse> {
  const { id, ...body } = payload
  const { data } = await providersUserPeople.partialUpdate(
    { id, provider_id: 'me' },
    { data: body },
  )
  return data
}

export const useUpdatePerson = () => {
  const client = useQueryClient()
  return useMutation(
    updatePerson,
    {
      // `useGetPerson` feeds both the EditPerson form and `PatientDetailTopBar`
      // (via the synthetic `patientUser` in `PersonDetail`). Invalidate
      // the retrieve key on success so the banner reflects edits like a renamed
      // first/last_name without a page refresh. `Number(...)` keeps the key
      // symmetric with `useGetPerson`: react-query compares keys by deep
      // equality, so `{id: 87062}` would not invalidate `{id: "87062"}`.
      onSuccess: async (_data, variables) => {
        await client.invalidateQueries({
          queryKey: providersUserPeople.retrieveQueryKey({
            id: Number(variables.id),
            provider_id: 'me',
          }),
        })
      },
    },
    { success: 'Patient info updated', error: 'Failed to update patient' },
  )
}

export const useGetPerson = (variables: { personId: number | null }) => {
  // Normalized to number to match the key `useUpdatePerson` invalidates.
  const pathParams = { id: Number(variables.personId), provider_id: 'me' }
  return useQuery(
    providersUserPeople.retrieveQueryKey(pathParams),
    async () => (await providersUserPeople.retrieve(pathParams)).data,
    // Skip the fetch when no person is available (e.g. a steerage whose
    // `person` is null): `Number(null)` is 0, which would otherwise request
    // the bogus `/people/0/` endpoint. Callers that pass a real id are
    // unaffected (enabled stays true).
    { enabled: variables.personId != null },
    {
      error: 'Failed to fetch person',
    },
  )
}
